import React, { useEffect, useState, useMemo } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, TextField, InputAdornment, IconButton, TableContainer,
    Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel,
    CircularProgress, Tooltip, Chip, Dialog, DialogTitle, DialogContent,
    DialogActions, Snackbar, Alert, Avatar, TablePagination
} from "@mui/material";
import {
    Search, Add, Edit, DeleteOutline, WarningAmber,
    FilterList
} from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    filterBg: "#FFF9E6",
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037'
};

const CourseListScreen = () => {
    const navigation = useNavigate();

    // --- State ---
    const [courseList, setCourseList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Search, Sorting & Pagination
    const [filterSearch, setFilterSearch] = useState("");
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('courseName');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // UI State
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [tempDeleteData, setTempDeleteData] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    useEffect(() => {
        getCourseList();
        localStorage.setItem("current_tab", "Class");
    }, []);

    const getCourseList = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/courses`);
            const resJson = await response.json();
            if (resJson.status === 1 || resJson.data) {
                setCourseList(resJson.data || []);
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Connection Error", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const deleteCourse = async (courseId) => {
        setLoadingDelete(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/courses/${courseId}`, {
                method: 'DELETE'
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setSnackbar({ open: true, message: "Class deleted successfully", severity: "success" });
                setOpenDeleteDialog(false);
                getCourseList();
            } else {
                alert(resJson.message);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        } finally {
            setLoadingDelete(false);
        }
    };

    // --- Table Logic ---
    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const filteredAndSortedData = useMemo(() => {
        return courseList
            .filter((item) => {
                const searchStr = filterSearch.toLowerCase();
                return (
                    item.courseName?.toLowerCase().includes(searchStr) ||
                    item.subject?.toLowerCase().includes(searchStr) ||
                    item.staffName?.toLowerCase().includes(searchStr) ||
                    item.class?.toLowerCase().includes(searchStr)
                );
            })
            .sort((a, b) => {
                const aValue = a[orderBy] || "";
                const bValue = b[orderBy] || "";
                return order === 'asc'
                    ? (aValue < bValue ? -1 : 1)
                    : (bValue < aValue ? -1 : 1);
            });
    }, [courseList, filterSearch, order, orderBy]);

    const columns = [
        { id: 'courseName', name: 'Class Name' },
        { id: 'class', name: 'Batch' },
        { id: 'subject', name: 'Subject' },
        { id: 'group', name: 'Group' },
        { id: 'staffName', name: 'Tutor' },
        { id: 'payment', name: 'Fee' },
        { id: 'status', name: 'Status' }
    ];

    const getStatusChip = (status) => {
        const isActive = status === "A";
        return (
            <Chip
                label={isActive ? "Active" : "Inactive"}
                color={isActive ? "success" : "default"}
                size="small"
                sx={{ fontWeight: 'bold', borderRadius: 1.5 }}
            />
        );
    };

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />
            <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>

                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                    <Typography color="text.primary">Class</Typography>
                </Breadcrumbs>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Header title="Class Management" subtitle="Manage courses, subjects, and tutor assignments" />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigation("/Portal/Class/AddClass")}
                        sx={{ bgcolor: colors.accent, color: 'black', borderRadius: 2, px: 3, '&:hover': { bgcolor: colors.accentDark } }}
                    >
                        Add Class
                    </Button>
                </Stack>

                <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                        <FilterList sx={{ color: colors.textMain }} />
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search by class name, subject or tutor..."
                            value={filterSearch}
                            onChange={(e) => { setFilterSearch(e.target.value); setPage(0); }}
                            sx={{ bgcolor: 'white', borderRadius: 2, maxWidth: 500 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Stack>
                </Paper>

                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: '55vh' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}
                                    >
                                        <TableSortLabel
                                            active={orderBy === column.id}
                                            direction={orderBy === column.id ? order : 'asc'}
                                            onClick={() => handleSortRequest(column.id)}
                                        >
                                            {column.name}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="center" sx={{ bgcolor: colors.tableHeader, fontWeight: 'bold', color: colors.textMain }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}><CircularProgress size={30} /></TableCell></TableRow>
                            ) : filteredAndSortedData.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>No class records found.</TableCell></TableRow>
                            ) : filteredAndSortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                <TableRow key={row.courseId} hover>
                                    <TableCell
                                        sx={{ fontWeight: 'bold', cursor: 'pointer' }}
                                        onClick={() => navigation(`/Portal/Class/StudentList/${row.courseId}/${row.courseName}`)}
                                    >
                                        {row.courseName}
                                    </TableCell>
                                    <TableCell>{row.class}</TableCell>
                                    <TableCell>{row.subject}</TableCell>
                                    <TableCell>{row.group}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: colors.accent, color: 'black' }}>
                                                {row.staffName?.split(" ")[0]?.charAt(0)}{row.staffName?.split(" ")[1]?.charAt(0)}
                                            </Avatar>
                                            <Typography variant="body2">{row.staffName}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell>RM {row.payment}</TableCell>
                                    <TableCell>{getStatusChip(row.status)}</TableCell>
                                    <TableCell align="center">
                                        <Stack direction="row" spacing={1} justifyContent="center">
                                            <Tooltip title="Edit Class">
                                                <IconButton size="small" onClick={() => navigation(`/Portal/Class/EditClass/${row.courseId}`)}>
                                                    <Edit fontSize="small" color="action" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete Class">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => { setTempDeleteData(row); setOpenDeleteDialog(true); }}
                                                >
                                                    <DeleteOutline fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={filteredAndSortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Box>

            {/* --- DELETE DIALOG --- */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} PaperProps={{ sx: { borderRadius: 4 } }}>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <WarningAmber sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <DialogTitle sx={{ fontWeight: 'bold', p: 0 }}>Confirm Delete</DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                        <Typography>Are you sure you want to delete <b>{tempDeleteData?.courseName}</b>?</Typography>
                        <Typography variant="caption" color="text.secondary">This action cannot be undone.</Typography>
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'center', gap: 2, mt: 2 }}>
                        <Button onClick={() => setOpenDeleteDialog(false)} color="inherit" variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={() => deleteCourse(tempDeleteData.courseId)}
                            disabled={loadingDelete}
                            sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                            {loadingDelete ? <CircularProgress size={20} /> : "Delete Class"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CourseListScreen;